import { API_BASE_URL } from '../config/api'
import { getAuthHeaders, getAuthHeadersAsync } from '../utils/authUtils'

async function adminFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeadersAsync(true)),
    ...(init.headers || {})
  }
  let response = await fetch(url, { ...init, headers })
  // Network blips / expired Firebase tokens often show up as 401 on the first try
  if (response.status === 401) {
    const retryHeaders = {
      'Content-Type': 'application/json',
      ...(await getAuthHeadersAsync(true)),
      ...(init.headers || {})
    }
    response = await fetch(url, { ...init, headers: retryHeaders })
  }
  return response
}

export interface PendingFoodBankUser {
  id: string
  email: string
  firstName: string
  lastName: string
  phone: string
  userType?: string
  isVerified: boolean
  emailVerified?: boolean
  phoneVerified?: boolean
  onboardingCompleted: boolean
  businessVerification?: any
  createdAt: string
}

export async function getPendingFoodBanks(): Promise<PendingFoodBankUser[]> {
  const response = await fetch(`${API_BASE_URL}/api/admin/pending-food-banks`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  })
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.error || 'Failed to load pending food banks')
  }
  return data.data || []
}

export async function verifyUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/verify`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  })
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.error || 'Failed to approve user')
  }
}

export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  })
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete user')
  }
}

export interface PendingBusinessAccount {
  id: string
  email: string
  firstName: string
  lastName: string
  phone: string
  userType: 'FOOD_BANK' | 'SUPPLIER'
  isVerified: boolean
  emailVerified: boolean
  phoneVerified: boolean
  onboardingCompleted: boolean
  createdAt: string
  organizationName?: string | null
  street?: string | null
  city?: string | null
  state?: string | null
  zipCode?: string | null
  businessVerification?: {
    id: string
    status: 'PENDING' | 'APPROVED' | 'REJECTED'
    organizationName: string
    website?: string | null
    ein?: string | null
    street: string
    city: string
    state: string
    zipCode: string
    submittedAt: string
    reviewedAt?: string | null
    reviewedById?: string | null
    rejectionReason?: string | null
  } | null
}

export async function getPendingAccounts(): Promise<PendingBusinessAccount[]> {
  const response = await fetch(`${API_BASE_URL}/api/admin/pending-accounts`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Failed to load pending accounts')
  return data.data || []
}

export async function approveAccount(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/accounts/${userId}/approve`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Failed to approve account')
}

export async function rejectAccount(userId: string, reason?: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/admin/accounts/${userId}/reject`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ reason })
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Failed to reject account')
}

export interface PendingVolunteerIdVerification {
  id: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  submittedAt: string
  reviewedAt?: string | null
  rejectionReason?: string | null
  user: {
    id: string
    email: string
    phone: string
    firstName: string
    lastName: string
    userType: string
  }
  image: {
    id: string
    filename: string
    mimeType: string
    size: number
    createdAt: string
  }
}

export async function getPendingVolunteerIds(): Promise<PendingVolunteerIdVerification[]> {
  const response = await fetch(`${API_BASE_URL}/api/admin/pending-volunteer-ids`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Failed to load pending volunteer IDs')
  return data.data || []
}

export async function approveVolunteerId(verificationId: string): Promise<{
  emailStatus?: 'sent' | 'skipped' | 'failed' | 'no_email'
  emailDetail?: string
}> {
  const response = await adminFetch(`${API_BASE_URL}/api/admin/volunteer-ids/${verificationId}/approve`, {
    method: 'PUT',
    body: JSON.stringify({})
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error((data as any).error || 'Failed to approve volunteer ID')
  return {
    emailStatus: (data as any).emailStatus,
    emailDetail: (data as any).emailDetail
  }
}

export async function rejectVolunteerId(verificationId: string, reason?: string): Promise<void> {
  const response = await adminFetch(`${API_BASE_URL}/api/admin/volunteer-ids/${verificationId}/reject`, {
    method: 'PUT',
    body: JSON.stringify({ reason })
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error((data as any).error || 'Failed to reject volunteer ID')
}

export async function fetchVolunteerIdImageBlob(verificationId: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/api/volunteer-verification/${verificationId}/image`, {
    headers: {
      ...getAuthHeaders()
    }
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Failed to load image' }))
    throw new Error((data as any).error || 'Failed to load image')
  }
  return await response.blob()
}

export interface PendingStory {
  id: string
  title: string
  content: string
  excerpt?: string
  imageUrl?: string
  tags: string[]
  isPublished: boolean
  isApproved: boolean
  approvedAt?: string
  approvedBy?: string
  authorId: string
  authorName: string
  viewCount: number
  createdAt: string
  updatedAt: string
  author: {
    firstName: string
    lastName: string
    userType: string
    email: string
  }
}

// Get stories pending approval (admin only)
export async function getPendingStories(): Promise<PendingStory[]> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories/pending`, {
      headers: {
        ...authHeaders
      }
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch pending stories')
    }
    
    return data.data
  } catch (error) {
    console.error('Error fetching pending stories:', error)
    return []
  }
}

// Approve a story (admin only)
export async function approveStory(storyId: string, isPublished: boolean = true): Promise<boolean> {
  try {
    const authHeaders = await getAuthHeadersAsync(true)
    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }
    
    const response = await fetch(`${API_BASE_URL}/api/stories/${storyId}/approve`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify({ isPublished })
    })
    
    const data = await response.json()
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to approve story')
    }
    
    return true
  } catch (error) {
    console.error('Error approving story:', error)
    return false
  }
}
